import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { File, SquareArrowOutUpRight, Upload } from "lucide-react";
import axios from "axios";

import * as api from "services/api";

import { DocumentCard } from "./DocumentCard";
import { GenerateContractModal } from "./GenerateContractModal";
import { Stepper } from "./Stepper";
import { UpdateCard } from "./UpdateCard";
import { UpdateStatusModal } from "./UpdateStatusModal";

import LoadingComponent from "components/LoadingComponent";
import { SelectInput } from "components/SelectInput";
import { StyledTable } from "components/StyledTable";

import ls from "Localization";

import { stringToMask, toStringCurrency } from "helpers/string";

import {
	CommentTextArea,
	ContractInfoContainer,
	ContractInfoTitleContainer,
	ContractNameCell,
	ContractSection,
	DocumentsAndCommentsContainer,
	DocumentsScrollContainer,
	FileErrorMessage,
	MainButton,
	RightSideButtonContainer,
	ScrollContainer,
	Separator,
	SolicitationContractContainer,
	StatusContainer,
	StatusLabel,
	StatusSelectContainer,
	Subtitle,
	Title,
	UpdatesCardsContainer,
	UpdatesContainer,
	UploadDocumentDescription,
	UploadDocumentInputContainer,
	UploadDocumentTitle,
	VerticalSeparator
} from "./styles";

const tableColumns = [
	{
		headerName: "Nome do contrato",
		align: "left",
		alignContent: "left",
		width: 150,
		content: value => (
			<ContractNameCell
				href={`/contracts/${value._id}`}
				target="_blank"
				rel="noopener noreferrer"
				clickable={Boolean(value?._id)}
			>
				{value.name || "-"}
				{value._id && <SquareArrowOutUpRight size={16} color="#534b99" />}
			</ContractNameCell>
		)
	},
	{
		field: "createdAt",
		headerName: "Data da geração",
		align: "left",
		alignContent: "left",
		width: 150,
		content: value => (
			<span>{value ? dayjs(value).format("DD/MM/YYYY") : "-"}</span>
		)
	},
	{
		field: "contractType",
		headerName: "Tipo de contrato",
		align: "left",
		alignContent: "left",
		width: 150,
		content: value => <span>{value ? ls[value] : "-"}</span>
	}
];

export default function ContractSolicitationDetail() {
	const [contractSolicitation, setContractSolicitation] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [newStatus, setNewStatus] = useState("");
	const [selectedFile, setSelectedFile] = useState(null);
	const [fileError, setFileError] = useState("");
	const [isUploadingDocument, setIsUploadingDocument] = useState(false);
	const [inMemoryDocuments, setInMemoryDocuments] = useState([]);
	const [isCommenting, setIsCommenting] = useState(false);
	const [comment, setComment] = useState("");
	const [inMemoryUpdates, setInMemoryUpdates] = useState([]);
	const [
		isGenerateContractModalOpen,
		setIsGenerateContractModalOpen
	] = useState(false);
	const [generatedContracts, setGeneratedContracts] = useState([]);

	const history = useHistory();

	const scrollContainerRef = useRef(null);

	const { token } = useSelector(s => s.login);

	const { id } = useParams();

	function scrollToBottom() {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTop =
				scrollContainerRef.current.scrollHeight;
		}
	}

	function handleFileChange(event) {
		const file = event.target.files[0];
		setFileError("");

		if (!file) {
			setSelectedFile(null);
			return;
		}

		const maxSize = 25 * 1024 * 1024; // 25mb

		if (file.size > maxSize) {
			setFileError("O arquivo deve ter no máximo 25MB");
			setSelectedFile(null);
			event.target.value = "";
			return;
		}

		if (file.type !== "application/pdf") {
			setFileError("Apenas arquivos PDF são permitidos");
			setSelectedFile(null);
			event.target.value = "";
			return;
		}

		setSelectedFile(file);
	}

	function handleStatusUpdate(event) {
		event.preventDefault();

		if (
			!newStatus ||
			newStatus === contractSolicitation?.contractSolicitation?.status
		) {
			toast.error("Selecione um status diferente do atual.");
			return;
		}

		setIsModalOpen(true);
	}

	async function handleUploadDocument() {
		try {
			setIsUploadingDocument(true);

			const formData = new FormData();
			formData.append("file", selectedFile);
			formData.append("contractSolicitation", id);

			const response = await axios.post(
				`${api.serverUrl}/contract-solicitation-document`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
						Authorization: "Bearer " + token
					}
				}
			);

			const result = response.data;

			if (result.isValid) {
				toast.success("Documento enviado com sucesso!");
				setSelectedFile(null);
				setInMemoryUpdates(prevState => [
					...prevState,
					result.model.contractSolicitationDocument
				]);
				setInMemoryDocuments(prevState => [
					...prevState,
					result.model.contractSolicitationDocument
				]);
				scrollToBottom();

				const fileInput = document.getElementById("document");

				if (fileInput) {
					fileInput.value = "";
				}
			} else {
				toast.error(
					"Houve um erro ao enviar o documento. Tente novamente mais tarde."
				);
			}
		} catch (error) {
			toast.error(
				"Houve um erro ao enviar o documento. Tente novamente mais tarde."
			);
			console.log(error);
		} finally {
			setIsUploadingDocument(false);
		}
	}

	async function handleCommentSubmit() {
		try {
			setIsCommenting(true);

			const response = await api.sendPost(
				"/contract-solicitation-comment",
				{
					contractSolicitation: id,
					commentText: comment
				},
				{
					Authorization: "Bearer " + token
				}
			);

			const result = await response.json();

			if (result.isValid) {
				toast.success("Comentário enviado com sucesso!");
				setComment("");
				setInMemoryUpdates(prevState => [
					...prevState,
					result.model.contractSolicitationComment
				]);
				scrollToBottom();
			} else {
				toast.error(
					"Houve um erro ao enviar o comentário. Tente novamente mais tarde."
				);
			}
		} catch (error) {
			toast.error(
				"Houve um erro ao enviar o comentário. Tente novamente mais tarde."
			);
			console.log(error);
		} finally {
			setIsCommenting(false);
		}
	}

	async function getContractSolicitation() {
		try {
			setIsLoading(true);

			const response = await api.sendGet(`/contract-solicitation/${id}`, {
				Authorization: "Bearer " + token
			});

			const result = await response.json();

			if (result.isValid) {
				setContractSolicitation(result.model);

				const contractResponse = await api.sendGet(
					`/contracts/solicitation/${result.model?.contractSolicitation?.solicitationId}`,
					{
						Authorization: "Bearer " + token
					}
				);

				const contractResult = await contractResponse.json();

				if (contractResult.isValid && contractResult.model?._id) {
					setGeneratedContracts([contractResult.model]);
				}
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		getContractSolicitation();
	}, [id]);

	useLayoutEffect(() => {
		scrollToBottom();
	}, [contractSolicitation]);

	useEffect(() => {
		if (!isLoading) {
			const timer = setTimeout(scrollToBottom, 100);
			return () => clearTimeout(timer);
		}
	}, [isLoading]);

	return (
		<Container className="dashboard">
			<Card>
				<CardBody>
					{isLoading ? (
						<LoadingComponent />
					) : (
						<>
							<Title>
								Solicitação{" "}
								{`#${String(
									contractSolicitation?.contractSolicitation?.solicitationId
								).padStart(6, "0")}`}
							</Title>

							<StatusContainer>
								<Subtitle>Status da solicitação</Subtitle>

								<StatusSelectContainer onSubmit={handleStatusUpdate}>
									<StatusLabel>Selecione o status da solicitação:</StatusLabel>
									<SelectInput
										name="status"
										options={[
											{ label: "Solicitado", value: "requested" },
											{ label: "Produção", value: "production" },
											{ label: "Ajustes", value: "adjustment" },
											{
												label: "Validação",
												value: "validation"
											},
											{ label: "Geração", value: "creation" },
											{ label: "Finalizado", value: "finished" }
										]}
										value={
											newStatus ||
											contractSolicitation?.contractSolicitation?.status
										}
										onChange={e => {
											setNewStatus(e.target.value);
										}}
									/>

									<MainButton type="submit">Salvar</MainButton>

									<UpdateStatusModal
										isModalOpen={isModalOpen}
										setIsModalOpen={setIsModalOpen}
										previousStatus={
											contractSolicitation?.contractSolicitation?.status
										}
										newStatus={newStatus}
										onStatusUpdated={getContractSolicitation}
									/>
								</StatusSelectContainer>
							</StatusContainer>

							<Stepper
								steps={contractSolicitation?.statusHistory}
								currentStatus={
									contractSolicitation?.contractSolicitation?.status
								}
							/>

							<Separator />

							<ContractInfoTitleContainer>
								<Subtitle>Informações do contrato</Subtitle>
								<MainButton
									onClick={() =>
										history.push(`/contracts/contract-solicitation/${id}/edit`)
									}
								>
									Editar
								</MainButton>
							</ContractInfoTitleContainer>

							<ContractInfoContainer>
								<ContractSection>
									<Row>
										<Col xs={12} sm={12} md={6} lg={6}>
											<strong>Empresa contratante</strong>
											<br />
											<span style={{ display: "flex", alignItems: "center" }}>
												{
													contractSolicitation?.contractSolicitation?.hirer
														?.businessName
												}
												<img
													src="/img/redirect.svg"
													alt=""
													style={{
														marginLeft: "8px",
														width: "14px",
														height: "14px",
														cursor: "pointer"
													}}
													onClick={() => {
														const hirerId =
															contractSolicitation?.contractSolicitation
																?.hirer?._id;
														if (hirerId) {
															history.push(`/companies/${hirerId}`);
														}
													}}
												/>
											</span>
										</Col>
									</Row>
									<Row>
										<Col xs={12} sm={12} md={9} lg={9}>
											<strong>Endereço</strong>
											<br />
											<span>
												{contractSolicitation?.contractSolicitation?.hirer
													?.address
													? (() => {
															const address =
																contractSolicitation.contractSolicitation
																	.hirer.address;
															const parts = [
																address.street,
																address.number
															].filter(Boolean);
															const location = [
																address.city,
																address.state
															].filter(Boolean);
															const streetPart = parts.length
																? parts.join(", ")
																: "";
															const locationPart = location.length
																? location.join(", ")
																: "";
															const fullAddress = [
																streetPart,
																locationPart
															]
																.filter(Boolean)
																.join(", ");
															return fullAddress || "Não informado";
													  })()
													: "Não informado"}
											</span>
										</Col>
										<Col xs={12} sm={12} md={3} lg={3}>
											<strong>CNPJ</strong>
											<br />
											<span>
												{contractSolicitation?.contractSolicitation?.hirer?.cnpj
													? stringToMask(
															contractSolicitation.contractSolicitation.hirer
																.cnpj,
															"cnpj"
													  )
													: "Não informado"}
											</span>
										</Col>
									</Row>
								</ContractSection>

								<Separator />

								<ContractSection>
									<Row>
									<Col xs={12} sm={12} md={6} lg={6}>
										<strong>Empresa prestadora</strong>
										<br />
										<span style={{ display: "flex", alignItems: "center" }}>
											{
												contractSolicitation?.contractSolicitation?.hired
													?.businessName
											}
											<img
												src="/img/redirect.svg"
												alt=""
												style={{
													marginLeft: "8px",
													width: "14px",
													height: "14px",
													cursor: "pointer"
												}}
												onClick={() => {
													const hiredId =
														contractSolicitation?.contractSolicitation
															?.hired?._id;
													if (hiredId) {
														history.push(`/companies/${hiredId}`);
													}
												}}
											/>
										</span>
									</Col>
								</Row>
								<Row>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Nome completo</strong>
										<br />
										<span style={{ display: "flex", alignItems: "center" }}>
											{contractSolicitation?.contractSolicitation?.hired
												?.personName || "Não informado"}
											<img
												src="/img/redirect.svg"
												alt=""
												style={{
													marginLeft: "8px",
													width: "14px",
													height: "14px",
													cursor: "pointer"
												}}
												onClick={() => {
													const hiredId =
														contractSolicitation?.contractSolicitation
															?.hired?.user;
													if (hiredId) {
														history.push(`/client/${hiredId}`);
													}
												}}
											/>
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>RG</strong>
										<br />
										<span>
											{stringToMask(
												contractSolicitation?.contractSolicitation?.hired?.rg,
												"rg"
											) || "Não informado"}
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>CPF</strong>
										<br />
										<span>
											{stringToMask(
												contractSolicitation?.contractSolicitation?.hired?.cpf,
												"cpf"
											) || "Não informado"}
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>CNPJ</strong>
										<br />
										<span>
											{contractSolicitation?.contractSolicitation?.hired?.cnpj
												? stringToMask(
														contractSolicitation?.contractSolicitation?.hired
															?.cnpj,
														"cnpj"
												  )
												: "Não informado"}
										</span>
									</Col>
								</Row>
								<Row>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Função</strong>
										<br />
										<span>
											{contractSolicitation?.contractSolicitation?.role}
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Remuneração</strong>
										<br />
										<span>
											{toStringCurrency(
												contractSolicitation?.contractSolicitation?.value / 100
											)}
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Data do pagamento</strong>
										<br />
										<span>
											{contractSolicitation?.contractSolicitation?.paymentDate}
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Data de início de vigência</strong>
										<br />
										<span>
											{dayjs(
												contractSolicitation?.contractSolicitation?.startDate
											).format("DD/MM/YYYY")}
										</span>
									</Col>
								</Row>
								<Row>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Duração do contrato</strong>
										<br />
										<span>
											{contractSolicitation?.contractSolicitation?.lifespan}{" "}
											{contractSolicitation?.contractSolicitation?.lifespan > 1
												? "meses"
												: "mês"}
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Modalidade</strong>
										<br />
										<span>
											{ls[contractSolicitation?.contractSolicitation?.modality]}
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Tipo de contrato</strong>
										<br />
										<span>
											{
												ls[
													contractSolicitation?.contractSolicitation
														?.contractType
												]
											}
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Contrato pré-existente</strong>
										<br />
										{contractSolicitation?.contractSolicitation
											?.baseContractFile ? (
											<a
												href={
													contractSolicitation?.contractSolicitation
														?.baseContractFile
												}
												target="_blank"
												rel="noreferrer"
												style={{
													color: "#000000",
													textDecoration: "underline"
												}}
											>
												Visualizar
											</a>
										) : (
											<span>Não informado</span>
										)}
									</Col>
								</Row>
								<Row>
									<Col xs={12} sm={12} md={6} lg={6}>
										<strong>Descrição das atividades</strong>
										<br />
										<span>
											{
												contractSolicitation?.contractSolicitation
													?.activitiesDescription
											}
										</span>
									</Col>
									<Col xs={12} sm={12} md={6} lg={6}>
										<strong>Detalhes adicionais</strong>
										<br />
										<span>
											{contractSolicitation?.contractSolicitation
												?.additionalInformation || "Não informado"}
										</span>
									</Col>
								</Row>
								<Row>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Contrato anterior (apenas para referência)</strong>
										<br />
										{contractSolicitation?.contractSolicitation
											?.contractReference?._id ? (
											<a
												href={`/contracts/${contractSolicitation.contractSolicitation.contractReference._id}`}
												target="_blank"
												rel="noopener noreferrer"
												style={{
													color: "#000000",
													textDecoration: "underline",
													cursor: "pointer"
												}}
											>
												{contractSolicitation.contractSolicitation
													.contractReference?.template?.name ||
													"Nome não informado"}
											</a>
										) : (
											<span>Não informado</span>
										)}
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Prioridade</strong>
										<br />
										<span>
											{(
												ls[
													contractSolicitation?.contractSolicitation?.priority
												] ||
												contractSolicitation?.contractSolicitation?.priority
											)?.toUpperCase()}
										</span>
									</Col>
									<Col xs={12} sm={6} md={3} lg={3}>
										<strong>Tier</strong>
										<br />
										<span>
											{contractSolicitation?.contractSolicitation?.tier
												?.charAt(0)
												.toUpperCase() +
												contractSolicitation?.contractSolicitation?.tier
													?.slice(1)
													.toLowerCase()}
										</span>
									</Col>
								</Row>
								</ContractSection>
							</ContractInfoContainer>

							<Separator />

							<UpdatesContainer>
								<UpdatesCardsContainer>
									<Subtitle>Atualizações</Subtitle>
									<ScrollContainer ref={scrollContainerRef}>
										{contractSolicitation?.updates?.map(update => (
											<UpdateCard key={update._id} update={update} />
										))}
										{inMemoryUpdates.map(update => (
											<UpdateCard key={update._id} update={update} />
										))}
									</ScrollContainer>
								</UpdatesCardsContainer>

								<VerticalSeparator />

								<DocumentsAndCommentsContainer>
									<Subtitle>Documentos</Subtitle>
									<DocumentsScrollContainer>
										{contractSolicitation?.documents?.map(document => (
											<DocumentCard
												key={document._id}
												document={document}
												token={token}
												onDocumentDeleted={() => {
													setInMemoryDocuments([]);
													setInMemoryUpdates([]);
													getContractSolicitation();
												}}
											/>
										))}
										{inMemoryDocuments.map(document => (
											<DocumentCard
												key={document._id}
												document={document}
												token={token}
												onDocumentDeleted={() => {
													setInMemoryDocuments([]);
													setInMemoryUpdates([]);
													getContractSolicitation();
												}}
											/>
										))}
									</DocumentsScrollContainer>

									<UploadDocumentInputContainer>
										{selectedFile ? (
											<>
												<File size={32} color="#31475a" strokeWidth={2} />
												<UploadDocumentTitle>
													{selectedFile.name}
												</UploadDocumentTitle>
												<UploadDocumentDescription>
													Clique no botão <strong>{"Enviar documento"}</strong>{" "}
													para fazer upload.
												</UploadDocumentDescription>
											</>
										) : (
											<>
												<Upload size={32} color="#31475a" strokeWidth={2} />
												<UploadDocumentTitle>
													Clique aqui para fazer upload do contrato
												</UploadDocumentTitle>
												<UploadDocumentDescription>
													(apenas .pdf 25 mb tamanho máximo)
												</UploadDocumentDescription>
											</>
										)}

										<input
											type="file"
											accept="application/pdf"
											id="document"
											onChange={handleFileChange}
										/>
										{fileError && (
											<FileErrorMessage>{fileError}</FileErrorMessage>
										)}
									</UploadDocumentInputContainer>

									<RightSideButtonContainer>
										<MainButton
											onClick={handleUploadDocument}
											disabled={isUploadingDocument || !selectedFile}
										>
											{isUploadingDocument ? (
												<Spinner size="sm" />
											) : (
												"Enviar documento"
											)}
										</MainButton>
									</RightSideButtonContainer>

									<Separator hasMargin={false} />

									<Subtitle>Comentário</Subtitle>

									<CommentTextArea
										placeholder="Descreva de forma objetiva seu comentário."
										value={comment}
										onChange={e => setComment(e.target.value)}
									/>

									<RightSideButtonContainer>
										<MainButton
											onClick={handleCommentSubmit}
											disabled={isCommenting || !comment.trim()}
										>
											{isCommenting ? (
												<Spinner size="sm" />
											) : (
												"Enviar comentário"
											)}
										</MainButton>
									</RightSideButtonContainer>
								</DocumentsAndCommentsContainer>
							</UpdatesContainer>

							<Separator />

							<SolicitationContractContainer>
								<Subtitle>Contrato da solicitação</Subtitle>
								<MainButton
									onClick={() => setIsGenerateContractModalOpen(true)}
									disabled={
										contractSolicitation?.contractSolicitation?.status !==
											"creation" || generatedContracts.length > 0
									}
								>
									Gerar contrato
								</MainButton>
							</SolicitationContractContainer>

							<StyledTable
								columns={tableColumns}
								data={generatedContracts}
								showPagination={false}
								rowClick={false}
							/>

							<GenerateContractModal
								isModalOpen={isGenerateContractModalOpen}
								setIsModalOpen={setIsGenerateContractModalOpen}
								solicitationId={String(
									contractSolicitation?.contractSolicitation?.solicitationId
								)}
								contractSolicitation={
									contractSolicitation?.contractSolicitation
								}
								lastDocumentName={
									contractSolicitation?.documents?.length > 0
										? contractSolicitation.documents[
												contractSolicitation.documents.length - 1
										  ]?.documentName
										: ""
								}
								lastDocumentURL={
									contractSolicitation?.documents?.length > 0
										? contractSolicitation.documents[
												contractSolicitation.documents.length - 1
										  ]?.documentUrl
										: ""
								}
								onContractGenerated={getContractSolicitation}
							/>
						</>
					)}
				</CardBody>
			</Card>
		</Container>
	);
}
